import type { SyncQueue } from './db';
import { offlineAttachmentRepository } from './offlineAttachmentRepository';
import { syncQueueMaintenanceRepository } from './syncQueueMaintenanceRepository';
class MaintenanceSyncService {
  private isSyncing = false;
  private MAX_RETRIES = 5;

  async syncPending(): Promise<void> {
    if (this.isSyncing) return;

    this.isSyncing = true;

    try {
      const pendingItems = await syncQueueMaintenanceRepository.getPending();

      if (pendingItems.length === 0) return;

      for (const item of pendingItems) {
        try {
          await syncQueueMaintenanceRepository.markAsSyncing(item.id);

          switch (item.type) {
            case 'execute': {
              await $fetch(`/api/maintenances/${item.maintenanceId}`, {
                method: 'PUT',
                body: {
                  maintenance: {
                    id: item.maintenanceId,
                    status: 'execution',
                    execution: {
                      startDate: new Date().toISOString()
                    }
                  }
                }
              });

              break;
            }

            case 'complete': {
              await this.syncCompleteMaintenance(item);
              break;
            }

            default: {
              throw new Error(`Unsupported sync type: ${item.type}`);
            }
          }

          await syncQueueMaintenanceRepository.markAsSynced(item.id);
        } catch (error) {
          console.error(`Error syncing queue item ${item.id}`, error);

          await syncQueueMaintenanceRepository.incrementRetries(item.id);

          await syncQueueMaintenanceRepository.markAsError(
            item.id,
            error instanceof Error ? error.message : 'Unknown error'
          );
        }
      }
    } finally {
      this.isSyncing = false;
    }
  }

  async syncSingleItem(itemId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const item = await syncQueueMaintenanceRepository.getById(itemId);
      if (!item) {
        return { success: false, error: 'Item não encontrado' };
      }

      if (item.retries >= this.MAX_RETRIES) {
        return { success: false, error: 'Limite de tentativas atingido' };
      }

      await syncQueueMaintenanceRepository.markAsSyncing(item.id);

      try {
        switch (item.type) {
          case 'execute': {
            await $fetch(`/api/maintenances/${item.maintenanceId}`, {
              method: 'PUT',
              body: {
                maintenance: {
                  id: item.maintenanceId,
                  status: 'execution',
                  execution: {
                    startDate: new Date().toISOString()
                  }
                }
              }
            });
            break;
          }

          case 'complete': {
            await this.syncCompleteMaintenance(item);
            break;
          }

          default: {
            throw new Error(`Unsupported sync type: ${item.type}`);
          }
        }

        await syncQueueMaintenanceRepository.markAsSynced(item.id);
        return { success: true };
      } catch (error) {
        console.error(`Error syncing single item ${item.id}`, error);
        await syncQueueMaintenanceRepository.incrementRetries(item.id);
        await syncQueueMaintenanceRepository.markAsError(
          item.id,
          error instanceof Error ? error.message : 'Erro desconhecido'
        );
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Erro desconhecido'
        };
      }
    } catch (error) {
      console.error(`Error in syncSingleItem`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro desconhecido'
      };
    }
  }

  private async syncCompleteMaintenance(item: SyncQueue): Promise<void> {
    if (!('data' in item.payload)) return;
    const payload = item.payload.data as {
      attachments: Array<{ category?: string; url: string }>;
    };

    const syncedAttachments: Array<{ category?: string; url: string }> = [];

    for (const attachment of payload.attachments) {
      if (!attachment.url.startsWith('offline://')) {
        syncedAttachments.push(attachment);
        continue;
      }

      const attachmentId = attachment.url.replace('offline://', '');

      const offlineAttachment = await offlineAttachmentRepository.getById(attachmentId);

      if (!offlineAttachment) {
        throw new Error(`Offline attachment not found: ${attachmentId}`);
      }

      const file = new File([offlineAttachment.file], offlineAttachment.originalName, {
        type: offlineAttachment.mimeType
      });

      const formData = new FormData();
      formData.append('file', file);

      const response = await $fetch<{ url: string }>('/api/upload-attachment', {
        method: 'POST',
        body: formData
      });

      syncedAttachments.push({
        category: attachment.category,
        url: response.url
      });

      await offlineAttachmentRepository.markAsUploaded(offlineAttachment.id, response.url);
    }

    await $fetch(`/api/maintenances/${item.maintenanceId}/complete`, {
      method: 'POST',
      body: {
        ...payload,
        attachments: syncedAttachments
      }
    });
  }
}

export const maintenanceSyncService = new MaintenanceSyncService();
